import axios from 'axios';
import store from '@/store'; // Import Vuex store to update tokens
import router from '@/router';
import { toast } from 'vue-toastification';
import { isTokenExpired } from './token';

const api = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_URL, // .env file
});

const AUTH_PATHS = ['/api/token/', '/api/token/refresh/'];

// one shared refresh call so parallel requests do not rotate the token twice
let refreshCall = null;

function isAuthPath(url) {
  return AUTH_PATHS.some((path) => (url || '').includes(path));
}

function refreshSession(refreshToken) {
  if (!refreshCall) {
    refreshCall = axios
      .post(`${process.env.VUE_APP_BACKEND_URL}/api/token/refresh/`, {
        refresh: refreshToken,
      })
      .then(({ data }) => {
        // update Vuex and localStorage with the new tokens
        store.commit('setAuthTokens', {
          access: data.access,
          refresh: data.refresh || refreshToken, // in case rotation is enabled
        });
        return data.access;
      })
      .finally(() => {
        refreshCall = null;
      });
  }
  return refreshCall;
}

// only a token the server actually refused ends the session, not a network blip
function wasRefused(error) {
  const code = error?.response?.status;
  return code === 400 || code === 401 || code === 403;
}

function endSession() {
  if (!store.state.isAuthenticated) return;
  store.commit('logout');
  toast.error('Your session has expired. Please log in again.');
  if (router.currentRoute.value.path !== '/login') {
    router.push('/login');
  }
}

// Add a request interceptor to include the token if available
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (token && isTokenExpired(token) && refreshToken && !isAuthPath(config.url)) {
      try {
        token = await refreshSession(refreshToken);
      } catch (refreshError) {
        if (wasRefused(refreshError)) endSession();
        throw refreshError;
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    // if 401 and request is not already a retry
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthPath(originalRequest.url)
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const newAccessToken = await refreshSession(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // If refresh fails, force a logout and notify  user
          if (wasRefused(refreshError)) endSession();
          return Promise.reject(refreshError);
        }
      }
      endSession();
    }
    return Promise.reject(error);
  }
);

export default api;
